// apps/roleforge-app/src/components/chat/MessageBubble.tsx
// Plan 3b-ii — Phase 1.3: Single message row in the chat transcript.
// Plan 3e-ii Task 7.2: Detect `[INVITE_AFFORDANCE]` in agent messages
// and render an inline invite affordance beneath. The token is an internal
// renderer control and must never be shown as transcript text.
//
// Issue #1219 — the transcript had no speaker names at all. Sighted users
// inferred the speaker from bubble colour and alignment; screen-reader users
// and anyone who cannot rely on colour got an undifferentiated wall of text.
// Every row now carries a short visible speaker name ("You" / "RoleForge")
// that is also what a screen reader announces before the message.

import { InlineInviteAffordance } from '../invitations/InlineInviteAffordance';

const INVITE_TOKEN = '[INVITE_AFFORDANCE]';
const INVITE_TOKEN_PATTERN = /\s*\[INVITE_AFFORDANCE\]\s*/g;

export const AGENT_SPEAKER_NAME = 'RoleForge';
export const USER_SPEAKER_NAME = 'You';

interface Props {
  role: 'user' | 'agent' | 'tool' | 'system';
  content: string;
  toolName?: string;
  isStreaming?: boolean;
  /** Plan 3e-ii Task 7.2: forwarded for the inline invite affordance. */
  auditId?: string;
  /**
   * Overrides the displayed speaker name. The stakeholder shell uses it so a
   * contributor sees who they are talking to rather than a generic label.
   */
  speakerName?: string;
  /** Workspace-default agent avatar, when the agent has one configured. */
  avatarUrl?: string | null;
}

export function MessageBubble({
  role,
  content,
  isStreaming = false,
  auditId,
  speakerName,
  avatarUrl,
}: Props) {
  // Internal messages are not customer-facing transcript content.
  if (role === 'system' || role === 'tool') return null;

  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl bg-plum text-[#0B0E14] px-4 py-2 text-sm">
          <span className="block text-xs font-semibold opacity-80">
            {speakerName ?? USER_SPEAKER_NAME}
          </span>
          <span className="block whitespace-pre-wrap">{content}</span>
          {isStreaming && <span className="ml-0.5 animate-pulse">▋</span>}
        </div>
      </div>
    );
  }

  // agent
  const hasInvite = content.includes(INVITE_TOKEN);
  const cleanText = hasInvite
    ? content.replace(INVITE_TOKEN_PATTERN, '\n\n').replace(/\n{3,}/g, '\n\n').trim()
    : content;

  return (
    <div className="flex items-start justify-start gap-2">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={`${speakerName ?? AGENT_SPEAKER_NAME} profile picture`}
          className="mt-1 h-8 w-8 shrink-0 rounded-full object-cover"
          data-testid="agent-avatar"
        />
      ) : (
        <div
          aria-hidden="true"
          className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-coral text-sm font-semibold text-[#0B0E14]"
          data-testid="agent-avatar-fallback"
        >
          {(speakerName ?? AGENT_SPEAKER_NAME).slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="max-w-[85%] rounded-2xl border border-line bg-paper text-ink px-4 py-2 text-sm">
        <span className="block text-xs font-semibold text-graphite">
          {speakerName ?? AGENT_SPEAKER_NAME}
        </span>
        <span className="block whitespace-pre-wrap">{cleanText}</span>
        {isStreaming && <span className="ml-0.5 animate-pulse">▋</span>}
        {hasInvite && auditId && <InlineInviteAffordance auditId={auditId} />}
      </div>
    </div>
  );
}
